import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { listMyAccounts, listMyWorkspaces } from "@/lib/workspaces.functions";
import {
  listCalendarEvents,
  upsertCalendarEvent,
  deleteCalendarEvent,
  setEventAction,
} from "@/lib/calendar.functions";
import { Checkbox } from "@/components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  ChevronLeft, ChevronRight, Plus, MapPin, Link as LinkIcon,
  Calendar as CalendarIcon, List, Trash2,
} from "lucide-react";
import { toast } from "sonner";
import { format, addMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, isSameMonth, isSameDay, parseISO } from "date-fns";

export const Route = createFileRoute("/_authenticated/calendar/")({
  component: CalendarPage,
});

type Event = {
  id: string;
  title: string;
  description: string | null;
  event_layer: "personal" | "program";
  event_type: string | null;
  starts_at: string;
  ends_at: string | null;
  is_all_day: boolean;
  location_type: string | null;
  location_value: string | null;
  color: string | null;
  account_id: string | null;
  program_template_id: string | null;
  workspace_id: string | null;
  recurrence_rule: string | null;
  cover_image_url: string | null;
  remind_before_hours: number | null;
};

function CalendarPage() {
  const qc = useQueryClient();
  const [cursor, setCursor] = useState(new Date());
  const [view, setView] = useState<"month" | "list">("month");
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<Event | null>(null);
  const [detailEvent, setDetailEvent] = useState<Event | null>(null);
  const [workspaceFilter, setWorkspaceFilter] = useState<string[]>([]); // empty = all

  const fetchAccts = useServerFn(listMyAccounts);
  const { data: acctData } = useQuery({ queryKey: ["my-accounts"], queryFn: () => fetchAccts() });
  const accountId = acctData?.accounts?.[0]?.id ?? null;

  const fetchWs = useServerFn(listMyWorkspaces);
  const { data: wsData } = useQuery({ queryKey: ["my-workspaces"], queryFn: () => fetchWs() });
  const workspaces = (wsData?.workspaces ?? []) as Array<{ id: string; program_name: string; account_id: string }>;
  const workspacesForAccount = useMemo(
    () => workspaces.filter((w) => !accountId || w.account_id === accountId),
    [workspaces, accountId]
  );
  const wsNameById = useMemo(() => {
    const m: Record<string, string> = {};
    workspaces.forEach((w) => { m[w.id] = w.program_name; });
    return m;
  }, [workspaces]);

  const monthStart = startOfMonth(cursor);
  const monthEnd = endOfMonth(cursor);
  const gridStart = startOfWeek(monthStart);
  const gridEnd = endOfWeek(monthEnd);

  const fetchEvents = useServerFn(listCalendarEvents);
  const { data: eventsData, isLoading } = useQuery({
    queryKey: ["calendar-events", accountId, gridStart.toISOString(), gridEnd.toISOString()],
    queryFn: () => fetchEvents({ data: { account_id: accountId!, from: gridStart.toISOString(), to: gridEnd.toISOString() } }),
    enabled: !!accountId,
  });
  const allEvents: Event[] = (eventsData?.events ?? []) as Event[];
  const events: Event[] = useMemo(() => {
    if (workspaceFilter.length === 0) return allEvents;
    const set = new Set(workspaceFilter);
    return allEvents.filter((e) => (e.workspace_id ? set.has(e.workspace_id) : false));
  }, [allEvents, workspaceFilter]);

  const days = useMemo(() => {
    const out: Date[] = [];
    let d = gridStart;
    while (d <= gridEnd) {
      out.push(d);
      d = addDays(d, 1);
    }
    return out;
  }, [gridStart, gridEnd]);

  const eventsByDay = useMemo(() => {
    const map: Record<string, Event[]> = {};
    events.forEach((e) => {
      const key = format(parseISO(e.starts_at), "yyyy-MM-dd");
      (map[key] ||= []).push(e);
    });
    return map;
  }, [events]);

  const upsertFn = useServerFn(upsertCalendarEvent);
  const deleteFn = useServerFn(deleteCalendarEvent);

  const handleDelete = async (id: string) => {
    if (!confirm("Delete this event?")) return;
    await deleteFn({ data: { id } });
    toast.success("Event deleted");
    setDetailEvent(null);
    qc.invalidateQueries({ queryKey: ["calendar-events"] });
  };

  return (
    <div className="container mx-auto p-6 space-y-4">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <CalendarIcon className="h-6 w-6" /> Promotion Calendar
          </h1>
          <p className="text-sm text-muted-foreground">Program promos, launches, and your own schedule.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setCursor(addMonths(cursor, -1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="text-sm font-medium min-w-[150px] text-center">
            {format(cursor, "MMMM yyyy")}
          </div>
          <Button variant="outline" size="icon" onClick={() => setCursor(addMonths(cursor, 1))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCursor(new Date())}>Today</Button>
          <div className="ml-2 flex border rounded-md">
            <Button variant={view === "month" ? "secondary" : "ghost"} size="sm" onClick={() => setView("month")}>
              <CalendarIcon className="h-4 w-4 mr-1" /> Month
            </Button>
            <Button variant={view === "list" ? "secondary" : "ghost"} size="sm" onClick={() => setView("list")}>
              <List className="h-4 w-4 mr-1" /> List
            </Button>
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="sm">
                Workspaces: {workspaceFilter.length === 0 ? "All" : `${workspaceFilter.length} selected`}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-64 p-2" align="end">
              <div className="flex items-center justify-between px-2 py-1.5">
                <span className="text-xs font-medium">Filter by workspace</span>
                {workspaceFilter.length > 0 && (
                  <button className="text-xs text-primary hover:underline" onClick={() => setWorkspaceFilter([])}>Clear</button>
                )}
              </div>
              <div className="max-h-64 overflow-auto space-y-1">
                {workspacesForAccount.length === 0 && (
                  <div className="text-xs text-muted-foreground px-2 py-2">No workspaces yet.</div>
                )}
                {workspacesForAccount.map((w) => {
                  const checked = workspaceFilter.includes(w.id);
                  return (
                    <label key={w.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-muted cursor-pointer text-sm">
                      <Checkbox
                        checked={checked}
                        onCheckedChange={(v) => {
                          setWorkspaceFilter((prev) =>
                            v ? [...prev, w.id] : prev.filter((x) => x !== w.id)
                          );
                        }}
                      />
                      <span className="truncate">{w.program_name}</span>
                    </label>
                  );
                })}
              </div>
            </PopoverContent>
          </Popover>
          <Button
            size="sm"
            disabled={!accountId}
            onClick={() => { setEditing(null); setEditorOpen(true); }}
          >
            <Plus className="h-4 w-4 mr-1" /> New event
          </Button>
        </div>
      </div>

      {isLoading && <div className="text-sm text-muted-foreground">Loading…</div>}

      {view === "month" && (
        <div className="border rounded-lg overflow-hidden bg-card">
          <div className="grid grid-cols-7 border-b bg-muted/50">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
              <div key={d} className="p-2 text-xs font-medium text-muted-foreground text-center">{d}</div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {days.map((day) => {
              const key = format(day, "yyyy-MM-dd");
              const dayEvents = eventsByDay[key] ?? [];
              const inMonth = isSameMonth(day, cursor);
              const isToday = isSameDay(day, new Date());
              return (
                <div
                  key={key}
                  className={`min-h-[110px] border-b border-r p-1.5 ${inMonth ? "" : "bg-muted/20 text-muted-foreground"}`}
                >
                  <div className={`text-xs font-medium mb-1 ${isToday ? "inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground h-5 w-5" : ""}`}>
                    {format(day, "d")}
                  </div>
                  <div className="space-y-0.5">
                    {dayEvents.slice(0, 3).map((e) => (
                      <button
                        key={e.id}
                        onClick={() => setDetailEvent(e)}
                        className="w-full text-left text-[11px] px-1.5 py-0.5 rounded truncate hover:opacity-80"
                        style={{ backgroundColor: e.color ?? (e.event_layer === "program" ? "hsl(var(--primary) / 0.15)" : "hsl(var(--secondary))"), color: "inherit" }}
                        title={e.title}
                      >
                        {!e.is_all_day && <span className="opacity-60 mr-1">{format(parseISO(e.starts_at), "HH:mm")}</span>}
                        {e.title}
                      </button>
                    ))}
                    {dayEvents.length > 3 && (
                      <div className="text-[10px] text-muted-foreground px-1">+{dayEvents.length - 3} more</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === "list" && (
        <div className="border rounded-lg divide-y bg-card">
          {events.length === 0 && <div className="p-6 text-sm text-muted-foreground text-center">No events in this range.</div>}
          {events.map((e) => (
            <button
              key={e.id}
              onClick={() => setDetailEvent(e)}
              className="w-full p-3 flex items-start gap-3 text-left hover:bg-muted/50 transition-colors"
            >
              <div className="text-center min-w-[60px]">
                <div className="text-xs uppercase text-muted-foreground">{format(parseISO(e.starts_at), "MMM")}</div>
                <div className="text-xl font-semibold">{format(parseISO(e.starts_at), "d")}</div>
                {!e.is_all_day && (
                  <div className="text-[10px] text-muted-foreground">{format(parseISO(e.starts_at), "HH:mm")}</div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Badge variant={e.event_layer === "program" ? "default" : "secondary"} className="text-[10px]">
                    {e.event_layer}
                  </Badge>
                  {e.event_type && <span className="text-xs text-muted-foreground">{e.event_type}</span>}
                </div>
                <div className="font-medium truncate">{e.title}</div>
                {e.description && (
                  <div className="text-xs text-muted-foreground line-clamp-2">{e.description}</div>
                )}
                {e.location_value && (
                  <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    {e.location_type === "url" ? <LinkIcon className="h-3 w-3" /> : <MapPin className="h-3 w-3" />}
                    <span className="truncate">{e.location_value}</span>
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
      )}

      <EventEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        initial={editing}
        accountId={accountId}
        workspaces={workspacesForAccount}
        onSave={async (payload) => {
          await upsertFn({ data: payload });
          toast.success(editing ? "Event updated" : "Event created");
          setEditorOpen(false);
          qc.invalidateQueries({ queryKey: ["calendar-events"] });
        }}
      />

      <EventDetailDialog
        event={detailEvent}
        workspaceName={detailEvent?.workspace_id ? wsNameById[detailEvent.workspace_id] ?? null : null}
        onOpenChange={(o) => { if (!o) setDetailEvent(null); }}
        onEdit={(e) => { setEditing(e); setDetailEvent(null); setEditorOpen(true); }}
        onDelete={handleDelete}
      />
    </div>
  );
}

function EventDetailDialog({
  event,
  workspaceName,
  onOpenChange,
  onEdit,
  onDelete,
}: {
  event: Event | null;
  workspaceName: string | null;
  onOpenChange: (o: boolean) => void;
  onEdit: (e: Event) => void;
  onDelete: (id: string) => void;
}) {
  const rsvpFn = useServerFn(setEventAction);
  if (!event) return null;
  return (
    <Dialog open={!!event} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Badge variant={event.event_layer === "program" ? "default" : "secondary"} className="text-[10px]">
              {event.event_layer}
            </Badge>
            {event.title}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          {workspaceName && (
            <div>
              <div className="text-xs text-muted-foreground">Workspace</div>
              <div><Badge variant="outline">{workspaceName}</Badge></div>
            </div>
          )}
          <div>
            <div className="text-xs text-muted-foreground">When</div>
            <div>
              {format(parseISO(event.starts_at), "PPP")}
              {!event.is_all_day && ` · ${format(parseISO(event.starts_at), "HH:mm")}`}
              {event.ends_at && ` – ${format(parseISO(event.ends_at), "HH:mm")}`}
            </div>
          </div>
          {event.description && (
            <div>
              <div className="text-xs text-muted-foreground">Details</div>
              <div className="whitespace-pre-wrap">{event.description}</div>
            </div>
          )}
          {event.location_value && (
            <div>
              <div className="text-xs text-muted-foreground">Location</div>
              {event.location_type === "url" ? (
                <a href={event.location_value} target="_blank" rel="noreferrer" className="text-primary underline break-all">
                  {event.location_value}
                </a>
              ) : (
                <div>{event.location_value}</div>
              )}
            </div>
          )}
          <div className="flex gap-2 pt-2">
            <Button size="sm" variant="outline" onClick={async () => { await rsvpFn({ data: { event_id: event.id, action_type: "rsvp_yes" } }); toast.success("Marked as going"); }}>Going</Button>
            <Button size="sm" variant="outline" onClick={async () => { await rsvpFn({ data: { event_id: event.id, action_type: "rsvp_maybe" } }); toast.success("Marked as maybe"); }}>Maybe</Button>
            <Button size="sm" variant="outline" onClick={async () => { await rsvpFn({ data: { event_id: event.id, action_type: "dismissed" } }); toast.success("Dismissed"); }}>Dismiss</Button>
          </div>
        </div>
        <DialogFooter>
          {event.event_layer === "personal" && (
            <>
              <Button variant="outline" size="sm" onClick={() => onDelete(event.id)}>
                <Trash2 className="h-4 w-4 mr-1" /> Delete
              </Button>
              <Button size="sm" onClick={() => onEdit(event)}>Edit</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function EventEditorDialog({
  open,
  onOpenChange,
  initial,
  accountId,
  workspaces,
  onSave,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  initial: Event | null;
  accountId: string | null;
  workspaces: Array<{ id: string; program_name: string; account_id: string }>;
  onSave: (payload: any) => Promise<void>;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [isAllDay, setIsAllDay] = useState(false);
  const [locationType, setLocationType] = useState<"none" | "url" | "in_person">("none");
  const [locationValue, setLocationValue] = useState("");
  const [eventType, setEventType] = useState("");
  const [workspaceId, setWorkspaceId] = useState<string>("none");
  const [saving, setSaving] = useState(false);

  // Initialize on open
  useMemo(() => {
    if (open) {
      setTitle(initial?.title ?? "");
      setDescription(initial?.description ?? "");
      setStartsAt(toLocalInput(initial?.starts_at) || toLocalInput(new Date().toISOString()));
      setEndsAt(toLocalInput(initial?.ends_at) ?? "");
      setIsAllDay(initial?.is_all_day ?? false);
      setLocationType((initial?.location_type as any) ?? "none");
      setLocationValue(initial?.location_value ?? "");
      setEventType(initial?.event_type ?? "");
      setWorkspaceId(initial?.workspace_id ?? "none");
    }
  }, [open, initial]);

  const handleSubmit = async () => {
    if (!title.trim() || !startsAt || !accountId) return;
    setSaving(true);
    try {
      await onSave({
        id: initial?.id,
        event_layer: "personal",
        account_id: accountId,
        workspace_id: workspaceId === "none" ? null : workspaceId,
        title: title.trim(),
        description: description.trim() || null,
        event_type: eventType.trim() || null,
        starts_at: new Date(startsAt).toISOString(),
        ends_at: endsAt ? new Date(endsAt).toISOString() : null,
        is_all_day: isAllDay,
        location_type: locationType,
        location_value: locationType === "none" ? null : (locationValue.trim() || null),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{initial ? "Edit event" : "New event"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Title</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Webinar prep, launch day…" />
          </div>
          <div>
            <Label>Description</Label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} />
          </div>
          <div>
            <Label>Workspace (optional)</Label>
            <Select value={workspaceId} onValueChange={setWorkspaceId}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">, None,</SelectItem>
                {workspaces.map((w) => (
                  <SelectItem key={w.id} value={w.id}>{w.program_name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Starts</Label>
              <Input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
            </div>
            <div>
              <Label>Ends (optional)</Label>
              <Input type="datetime-local" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={isAllDay} onChange={(e) => setIsAllDay(e.target.checked)} />
            All day
          </label>
          <div>
            <Label>Type (optional)</Label>
            <Input value={eventType} onChange={(e) => setEventType(e.target.value)} placeholder="launch, webinar, content…" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div>
              <Label>Location</Label>
              <Select value={locationType} onValueChange={(v: any) => setLocationType(v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="url">URL</SelectItem>
                  <SelectItem value="in_person">In person</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {locationType !== "none" && (
              <div className="col-span-2">
                <Label>&nbsp;</Label>
                <Input value={locationValue} onChange={(e) => setLocationValue(e.target.value)} placeholder={locationType === "url" ? "https://…" : "Address or room"} />
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSubmit} disabled={saving || !title.trim() || !accountId}>
            {saving ? "Saving…" : initial ? "Save" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
